import {
  ParametrosPedido3D, LineaPedido3D, ResultadoPedido3D, ResultadoLinea3D,
} from '../types';

// ── Constantes de apoyo para la interfaz ─────────────────────────────────────

export const MATERIALES_3D = [
  'PLA', 'PLA Silk', 'PETG', 'ASA', 'ABS', 'TPU',
  'PC', 'PA / Nylon', 'PLA-CF', 'PETG-CF', 'Resina', 'Otro',
];

export const IMPRESORAS_PRESET: { nombre: string; vatios: number }[] = [
  { nombre: 'Bambu A1 mini', vatios: 70 },
  { nombre: 'Bambu A1', vatios: 90 },
  { nombre: 'Bambu P1S', vatios: 105 },
  { nombre: 'Bambu X1C', vatios: 120 },
  { nombre: 'Bambu H2D', vatios: 200 },
  { nombre: 'Ender 3 V3', vatios: 60 },
  { nombre: 'Prusa MK4 (con cámara)', vatios: 150 },
];

export const MONEDAS_3D = [
  { valor: '€', etiqueta: '€ Euro' },
  { valor: '$', etiqueta: '$ Dólar' },
  { valor: '£', etiqueta: '£ Libra' },
  { valor: 'MXN$', etiqueta: 'MXN$ Peso mexicano' },
  { valor: 'ARS$', etiqueta: 'ARS$ Peso argentino' },
  { valor: 'COP$', etiqueta: 'COP$ Peso colombiano' },
  { valor: 'CLP$', etiqueta: 'CLP$ Peso chileno' },
];

/** Tramos de multiplicador sugeridos según el tamaño del pedido. */
export const TRAMOS_MULTIPLICADOR = [
  { mult: 4,   etiqueta: '1 ud' },
  { mult: 3.5, etiqueta: '2–5' },
  { mult: 3,   etiqueta: '6–15' },
  { mult: 2.5, etiqueta: '16–50' },
  { mult: 2,   etiqueta: '50+' },
];

/** Multiplicador sugerido para un número total de unidades. */
export function multiplicadorSugerido(unidades: number): number {
  if (unidades <= 1)  return 4;
  if (unidades <= 5)  return 3.5;
  if (unidades <= 15) return 3;
  if (unidades <= 50) return 2.5;
  return 2;
}

let contadorLineas = 0;
export function nuevoIdLinea(): string {
  contadorLineas += 1;
  return `l${Date.now().toString(36)}${contadorLineas.toString(36)}`;
}

export function lineaNueva(nombre = ''): LineaPedido3D {
  return {
    id: nuevoIdLinea(),
    nombre,
    unidades: 1,
    base: 'pieza',
    piezasPorPlato: 1,
    gramos: 25,
    horas: 1,
    minutos: 20,
    material: 'PLA',
    precioRollo: 20,
    pesoRollo: 1000,
    merma: 5,
    cambiosColor: 0,
    purgaPorCambio: 4,
    minPostproceso: 3,
    extras: [],
    precioObjetivo: 10,
  };
}

export function parametrosIniciales(): ParametrosPedido3D {
  return {
    nombreTrabajo: '',
    moneda: '€',
    lineas: [lineaNueva()],

    vatios: 90,
    precioKwh: 0.18,
    costeImpresora: 350,
    vidaUtil: 4000,
    mantenimiento: 15,
    tasaFallo: 5,

    // Tarifa y extras arrancan a 0 a proposito: son decisiones tuyas, no
    // conviene que la calculadora sugiera un importe que se cuele sin mirarlo.
    tarifaHora: 0,
    minDiseno: 0,
    minPreparacion: 10,
    minPorPlato: 5,

    extras: [{ nombre: 'Embalaje', importe: 0 }],

    modo: 'margen',
    multiplicador: 4,
    iva: 21,
    redondeo: 0,
  };
}

// ── Formato ──────────────────────────────────────────────────────────────────

export function formatearImporte(valor: number, moneda: string, decimales = 2): string {
  const v = isFinite(valor) ? valor : 0;
  const s = v.toLocaleString('es-ES', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  });
  return moneda.length > 1 ? `${moneda} ${s}` : `${s} ${moneda}`;
}

export function formatearPorcentaje(valor: number): string {
  return `${(isFinite(valor) ? valor : 0).toLocaleString('es-ES', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })} %`;
}

export function formatearDuracion(horas: number): string {
  const total = Math.round((isFinite(horas) ? horas : 0) * 60);
  return `${Math.floor(total / 60)} h ${String(total % 60).padStart(2, '0')} min`;
}

// ── Cálculo ──────────────────────────────────────────────────────────────────

const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return isFinite(n) ? n : 0;
};

const aplicarRedondeo = (precio: number, redondeo: number): number => {
  if (redondeo === 0.95) return Math.max(0, Math.ceil(precio) - 0.05);
  if (redondeo > 0)      return Math.ceil(precio / redondeo) * redondeo;
  return precio;
};

/** Coste variable de una línea, antes de repartirle los fijos del pedido. */
interface ParcialLinea {
  linea: LineaPedido3D;
  unidades: number;
  platos: number;
  costeFilamento: number;
  costeElectricidad: number;
  costeMaquina: number;
  costeManoObra: number;
  costeExtras: number;
  costeVariable: number;
  gramos: number;
  horasMaquina: number;
  minutosPropios: number;
}

function calcularLinea(l: LineaPedido3D, p: ParametrosPedido3D, factorFallo: number): ParcialLinea {
  const unidades = Math.max(1, Math.round(num(l.unidades)));
  const piezasPorPlato = Math.max(1, Math.round(num(l.piezasPorPlato)));
  const esPlato = l.base === 'plato';

  // Datos del slicer llevados a "una pieza"
  const gramosPieza = esPlato ? num(l.gramos) / piezasPorPlato : num(l.gramos);
  const horasPieza = esPlato
    ? (num(l.horas) + num(l.minutos) / 60) / piezasPorPlato
    : num(l.horas) + num(l.minutos) / 60;

  // Los fallos obligan a imprimir más piezas, y por tanto más platos.
  const unidadesImpresas = unidades * factorFallo;
  const platos = Math.ceil(unidadesImpresas / piezasPorPlato);

  // Material: la merma escala con las piezas; la purga de cambios de color es
  // por plato, así que un plato a medio llenar purga lo mismo que uno lleno.
  const gramosPiezas = gramosPieza * (1 + num(l.merma) / 100) * unidadesImpresas;
  const gramosPurga = num(l.cambiosColor) * num(l.purgaPorCambio) * platos;
  const gramos = gramosPiezas + gramosPurga;
  const pesoRollo = Math.max(1, num(l.pesoRollo));
  const costeFilamento = gramos / pesoRollo * num(l.precioRollo);

  // Máquina
  const horasMaquina = horasPieza * unidadesImpresas;
  const costeElectricidad = (num(p.vatios) / 1000) * horasMaquina * num(p.precioKwh);
  const vidaUtil = Math.max(1, num(p.vidaUtil));
  const amortizacion = (num(p.costeImpresora) / vidaUtil) * horasMaquina;
  const costeMaquina = amortizacion * (1 + num(p.mantenimiento) / 100);

  // Tu tiempo: el post-procesado es sobre las piezas buenas que entregas;
  // el manejo del plato sí escala con los platos que llegas a lanzar.
  const minutosPropios = num(l.minPostproceso) * unidades + num(p.minPorPlato) * platos;
  const costeManoObra = minutosPropios / 60 * num(p.tarifaHora);

  const costeExtras = (l.extras || []).reduce((s, x) => s + num(x.importe), 0) * unidades;

  return {
    linea: l,
    unidades,
    platos,
    costeFilamento,
    costeElectricidad,
    costeMaquina,
    costeManoObra,
    costeExtras,
    costeVariable: costeFilamento + costeElectricidad + costeMaquina + costeManoObra + costeExtras,
    gramos,
    horasMaquina,
    minutosPropios,
  };
}

/**
 * Calcula un pedido completo.
 *
 * Los costes fijos del pedido (diseño, preparación y extras como el envío) se
 * reparten entre las líneas a prorrata de su coste variable: el artículo grande
 * absorbe más envío que el pequeño. El total del pedido no depende de ese
 * reparto; solo cambia el precio que sale para cada línea.
 */
export function calcularPedido3D(p: ParametrosPedido3D): ResultadoPedido3D {
  const tasaFallo = Math.min(90, Math.max(0, num(p.tasaFallo))) / 100;
  const factorFallo = 1 / (1 - tasaFallo);

  const lineas = (p.lineas || []).length ? p.lineas : [lineaNueva()];
  const parciales = lineas.map(l => calcularLinea(l, p, factorFallo));

  // Fijos del pedido
  const minutosFijos = num(p.minDiseno) + num(p.minPreparacion);
  const costeFijoTiempo = minutosFijos / 60 * num(p.tarifaHora);
  const costeFijoExtras = (p.extras || []).reduce((s, x) => s + num(x.importe), 0);
  const costeFijoPedido = costeFijoTiempo + costeFijoExtras;

  const totalVariable = parciales.reduce((s, x) => s + x.costeVariable, 0);
  const unidadesTotales = parciales.reduce((s, x) => s + x.unidades, 0);

  const resultadoLineas: ResultadoLinea3D[] = parciales.map(x => {
    // A prorrata del coste variable. Si todo vale cero (un pedido recién
    // empezado), se reparte por unidades para no dividir entre cero.
    const peso = totalVariable > 0
      ? x.costeVariable / totalVariable
      : (unidadesTotales > 0 ? x.unidades / unidadesTotales : 1 / parciales.length);
    const cuotaFija = costeFijoPedido * peso;

    const costeTotal = x.costeVariable + cuotaFija;
    const costeUnidad = costeTotal / x.unidades;

    let precioUnidad: number;
    let multiplicadorAplicado: number;

    if (p.modo === 'precio') {
      precioUnidad = num(x.linea.precioObjetivo);
      multiplicadorAplicado = costeUnidad > 0 ? precioUnidad / costeUnidad : 0;
    } else {
      const mult = x.linea.multiplicador !== undefined && x.linea.multiplicador !== null
        ? num(x.linea.multiplicador)
        : num(p.multiplicador);
      precioUnidad = aplicarRedondeo(costeUnidad * mult, num(p.redondeo));
      multiplicadorAplicado = costeUnidad > 0 ? precioUnidad / costeUnidad : mult;
    }

    const iva = num(p.iva) / 100;
    const beneficioUnidad = precioUnidad - costeUnidad;

    return {
      id: x.linea.id,
      nombre: x.linea.nombre,
      unidades: x.unidades,
      platos: x.platos,

      costeFilamento: x.costeFilamento,
      costeElectricidad: x.costeElectricidad,
      costeMaquina: x.costeMaquina,
      costeManoObra: x.costeManoObra,
      costeExtras: x.costeExtras,
      cuotaFija,

      costeUnidad,
      costeTotal,

      multiplicadorAplicado,
      precioUnidad,
      precioUnidadConIva: precioUnidad * (1 + iva),
      precioTotalConIva: precioUnidad * (1 + iva) * x.unidades,
      beneficioUnidad,
      beneficioTotal: beneficioUnidad * x.unidades,
      margenSobrePrecio: precioUnidad > 0 ? beneficioUnidad / precioUnidad * 100 : 0,

      gramos: x.gramos,
      horasMaquina: x.horasMaquina,
    };
  });

  // ── Totales del pedido ─────────────────────────────────────────────────────
  const suma = (f: (x: ParcialLinea) => number) => parciales.reduce((s, x) => s + f(x), 0);

  const costeTotal = resultadoLineas.reduce((s, l) => s + l.costeTotal, 0);
  const precioTotal = resultadoLineas.reduce((s, l) => s + l.precioUnidad * l.unidades, 0);
  const precioTotalConIva = resultadoLineas.reduce((s, l) => s + l.precioTotalConIva, 0);
  const beneficioTotal = precioTotal - costeTotal;

  const horasMaquina = suma(x => x.horasMaquina);
  const horasPropias = (minutosFijos + suma(x => x.minutosPropios)) / 60;
  const gramosTotales = suma(x => x.gramos);
  const costeFilamentoTotal = suma(x => x.costeFilamento);

  // Desglose del pedido: la mano de obra y los extras fijos van a su categoría
  const costeManoObra = suma(x => x.costeManoObra) + costeFijoTiempo;
  const costeExtras = suma(x => x.costeExtras) + costeFijoExtras;

  // ── Avisos ─────────────────────────────────────────────────────────────────
  const avisos: string[] = [];
  const multMedio = costeTotal > 0 ? precioTotal / costeTotal : 0;

  const enPerdidas = resultadoLineas.filter(l => l.beneficioUnidad < 0);
  if (enPerdidas.length) {
    const l = enPerdidas[0];
    avisos.push(
      `«${l.nombre || 'Línea sin nombre'}» se vende por debajo de coste: pierdes ` +
      `${formatearImporte(-l.beneficioUnidad, p.moneda)} por unidad.`,
    );
  } else if (multMedio > 0 && multMedio < 1.5) {
    avisos.push('Margen muy justo: por debajo de ×1,5 un solo fallo se come el beneficio.');
  }
  const platosAMedias = parciales.filter(x =>
    x.platos > 0 && num(x.linea.cambiosColor) > 0 &&
    x.unidades * factorFallo < x.platos * Math.max(1, Math.round(num(x.linea.piezasPorPlato))) - 0.01,
  );
  if (platosAMedias.length) {
    const x = platosAMedias[0];
    avisos.push(
      `«${x.linea.nombre || 'Una línea'}» deja un plato a medio llenar y la purga se paga igual. ` +
      `Ajustar las unidades a un múltiplo de ${Math.max(1, Math.round(num(x.linea.piezasPorPlato)))} sale más a cuenta.`,
    );
  }
  if (parciales.every(x => num(x.linea.merma) === 0 && num(x.linea.cambiosColor) === 0)) {
    avisos.push('Sin purga ni merma: casi ninguna impresión aprovecha el 100 % del filamento.');
  }

  return {
    lineas: resultadoLineas,

    costeFilamento: costeFilamentoTotal,
    costeElectricidad: suma(x => x.costeElectricidad),
    costeManoObra,
    costeMaquina: suma(x => x.costeMaquina),
    costeExtras,

    unidadesTotales,
    platosTotales: suma(x => x.platos),
    costeTotal,
    precioTotal,
    precioTotalConIva,
    beneficioTotal,
    margenSobrePrecio: precioTotal > 0 ? beneficioTotal / precioTotal * 100 : 0,

    horasMaquina,
    horasPropias,
    gramosTotales,
    costeFilamentoTotal,
    beneficioPorHoraMaquina: horasMaquina > 0 ? beneficioTotal / horasMaquina : 0,

    avisos,
  };
}

/** Texto plano del presupuesto, para copiar al portapapeles o enviar. */
export function resumenTexto(p: ParametrosPedido3D, r: ResultadoPedido3D): string {
  const f = (v: number) => formatearImporte(v, p.moneda);
  const L: string[] = [];

  L.push(`${p.nombreTrabajo || 'Pedido sin nombre'} — ${r.unidadesTotales} ud${r.unidadesTotales === 1 ? '' : 's'}`);
  L.push('');

  r.lineas.forEach(l => {
    L.push(`${l.nombre || 'Línea sin nombre'} — ${l.unidades} ud${l.unidades === 1 ? '' : 's'} (${l.platos} plato${l.platos === 1 ? '' : 's'})`);
    L.push(`  Coste por unidad: ${f(l.costeUnidad)}`);
    L.push(`  Precio por unidad: ${f(l.precioUnidad)}${num(p.iva) > 0 ? `  (${f(l.precioUnidadConIva)} con IVA)` : ''}`);
    L.push(`  Subtotal: ${f(l.precioTotalConIva)}`);
    L.push('');
  });

  L.push('Desglose del pedido');
  L.push(`  Filamento: ${f(r.costeFilamento)}`);
  L.push(`  Electricidad: ${f(r.costeElectricidad)}`);
  L.push(`  Mano de obra: ${f(r.costeManoObra)}`);
  L.push(`  Máquina: ${f(r.costeMaquina)}`);
  L.push(`  Extras: ${f(r.costeExtras)}`);
  L.push(`  Coste total: ${f(r.costeTotal)}`);
  L.push('');
  L.push(`TOTAL${num(p.iva) > 0 ? ' (con IVA)' : ''}: ${f(r.precioTotalConIva)}`);
  L.push(`Beneficio: ${f(r.beneficioTotal)}  ·  margen ${formatearPorcentaje(r.margenSobrePrecio)}`);
  L.push(`Horas de impresora: ${formatearDuracion(r.horasMaquina)}  ·  ${r.platosTotales} plato${r.platosTotales === 1 ? '' : 's'}  ·  Filamento: ${r.gramosTotales.toFixed(0)} g`);

  return L.join('\n');
}
